<template>
  <div class="panel my-account-info">
    <h2 class="flex-row flex-align-center gap-sm">
      <div class="dot">3</div>
      <div>{{ props.content.account_header }}</div>
      <a href="#" v-if="step === 5" @click.prevent="step -= 1">
        <i class="pi pi-pen-to-square"></i>
      </a>
    </h2>
    <template v-if="join.info.kit && join.info.rep">
      <div class="info-section">
        <div
          v-if="join.info.account"
          class="auto-form sub-form hrow info-column"
        >
          <div class="name">
            {{ join.accountData.first_name }} {{ join.accountData.last_name }}
          </div>

          <div>
            {{ join.addressData.address }}
          </div>
          <div v-if="join.addressData.address2">
            {{ join.addressData.address2 }}
          </div>

          <div>
            {{ join.addressData.city }}, {{ join.addressData.state }}
            {{ join.addressData.zip_code }}
          </div>
          <div>{{ join.accountData.email }}</div>
          <div>{{ join.accountData.phone }}</div>
        </div>

        <div>
          <div v-if="join.info.payout">
            <div
              v-if="
                join.payoutData.payout_method === join.methodOptions[0].value
              "
            >
              <div>{{ join.payoutData.bank_name }}</div>
              <div>{{ join.encryptedAccountNumber() }}</div>
            </div>

            <div v-else>
              <div>Check</div>
            </div>
          </div>

          <div v-if="join.info.password">
            <div>Password</div>
            <div>*********</div>
          </div>
        </div>
      </div>

      <div class="auto-form sub-form hrow">
        <PapaAutoForm
          v-if="step === 0"
          v-model="join.accountData"
          :fields="accountFields"
          @submit="step += 1"
          class="account-form"
        >
          <div class="m-sm-t">
            <Button type="submit" class="grey">Next</Button>
          </div>
        </PapaAutoForm>

        <PapaAutoForm
          v-else-if="step === 1"
          v-model="join.addressData"
          :fields="addresses.fields"
          @submit="
            join.info.account = true;
            step += 1;
          "
        >
          <div class="m-sm-t">
            <Button @click.prevent="step -= 1" class="grey prev-grey">
              Prev
            </Button>
            <Button type="submit" class="grey">Next</Button>
          </div>
        </PapaAutoForm>

        <div v-else-if="step === 2">
          <h3>PAYOUT INFORMATION</h3>
          <p>
            Please select how you would like to recieve your commissions.
            Commissions are paid monthly either by Direct Deposit (FREE) or
            Check ($2.50 fee).
          </p>
          <PapaAutoForm
            v-model="join.payoutData"
            :fields="join.payoutFields"
            @submit="
              join.info.payout = true;
              step += 1;
            "
          >
            <div class="m-sm-t">
              <Button @click.prevent="step -= 1" class="grey prev-grey">
                Prev
              </Button>
              <Button type="submit" class="grey"> Next </Button>
            </div>
          </PapaAutoForm>
        </div>

        <div v-else-if="step === 3">
          <h3>BACK OFFICE PASSWORD</h3>
          <p>
            This is the password that you, as a Consultant, will use to access
            your Back Office and shopping cart.
          </p>
          <PapaAutoForm
            v-model="join.passwordData"
            :fields="passwordFields"
            :errors="passwordErrors"
            @submit="verifyPasswordForm"
          >
            <div class="m-sm-t">
              <Button @click.prevent="step -= 1" class="grey prev-grey">
                Prev
              </Button>
              <Button type="submit" class="grey">Next</Button>
            </div>
          </PapaAutoForm>
        </div>

        <div v-else-if="step === 4" class="enrollment-terms">
          <h3 class="enrollment-header">Consultant Agreement</h3>
          <div class="terms m-sm-b" @scroll="handleTermsScroll">
            <div v-html="terms"></div>
          </div>
          <PapaAutoForm
            v-model="join.flagData"
            @submit="finishForms"
            :fields="[
              {
                name: 'terms_agreed',
                type: 'checkbox',
                required: true,
                disabled: !hasScrolledToBottomOfTerms,
                label:
                  'I agree to the Enrollment Terms & Conditions as presented here.',
              },
              {
                name: 'send_email',
                type: 'checkbox',
                required: false,
                label: 'Email me the Enrollment Terms and Conditions',
              },
            ]"
          >
            <div class="m-sm-t">
              <Button @click.prevent="step -= 1" class="grey prev-grey">
                Prev
              </Button>
              <Button type="submit" class="grey"> Next </Button>
            </div>
          </PapaAutoForm>
        </div>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts" generic="T extends Record<string, string>">
import { useAddressStore } from "@/stores/address";
import { useContentStore } from "@/stores/content";
import { useJoinStore } from "@/stores/join";
import { useUserStore } from "@/stores/user";
import { debounce } from "@/utils/bouncer";
import { validatePassword } from "@/utils/password_validation";
import api from "@virgodev/bazaar/functions/api";
import type { FieldDef } from "@virgodev/vue-models/components/forms/defs";
import Button from "primevue/button";
import { computed, onMounted, ref, watch } from "vue";
import PapaAutoForm from "../PapaAutoForm.vue";

const join = useJoinStore();
const content = useContentStore();
const addresses = useAddressStore();
const user = useUserStore();

const props = defineProps<{ content: T }>();

const step = ref(0);

const accountFields = [
  {
    label: false,
    type: "sub",
    css_classes: "hrow",
    fields: [
      { name: "first_name", type: "text", label: "First Name", required: true },
      { name: "last_name", type: "text", label: "Last Name", required: true },
    ],
  },
  {
    label: false,
    type: "sub",
    css_classes: "hrow",
    fields: [
      { name: "dob", type: "date", label: "Date of Birth", required: true },
      {
        name: "ssn",
        type: "masked",
        extra: {
          mask: "999-99-9999",
          tooltip:
            "As a Paparazzi Consultant you have the potential to generate income.",
        },
        pattern: "[^_]+",
        minlength: 12,
        label: "Social Security Number (NO EINS)",
        required: true,
        css_classes: "_lr-hide",
      },
    ],
  },
  {
    label: false,
    type: "sub",
    css_classes: "hrow",
    fields: [
      { name: "email", type: "email", label: "Email", required: true },
      { name: "phone", type: "phone", label: "Phone Number", required: true },
    ],
  },
  {
    label: false,
    type: "sub",
    css_classes: "hrow",
    fields: [
      {
        name: "language",
        label: "Preferred Language",
        options: [
          { value: "1", name: "English" },
          { value: "98", name: "Spanish" },
        ],
        default: "1",
        required: true,
      },
    ],
  },
];
const passwordFields: FieldDef[] = [
  {
    name: "back_office_password",
    label: "Back Office Password",
    type: "new-password",
    ariaLabel: "Password",
    required: true,
    autocomplete: "new-password",
    placeholder:
      "Must be at least 8 characters. You may use letters, numbers, and  . + - _ characters.",
  },
  {
    name: "confirm_password",
    label: "Confirm Password",
    type: "password",
    required: true,
    autocomplete: "new-password",
    ariaLabel: "Confirm Password",
    placeholder: "Same as Above",
  },
];
const passwordErrors = ref<Record<string, string[]>>({});

const hasScrolledToBottomOfTerms = ref(false);

const terms = computed(() => {
  return content.snippets.find((s) => {
    return s.section.name == "new-consultant-terms";
  })?.content;
});

watch(
  () => join.addressData.zip_code,
  () => {
    addresses.fetchCountyInfo(join.addressData.zip_code);
  },
);

watch(step, () => {
  join.info.accountInfoComplete = step.value > 4;
});

// check for WA registrations
watch(
  () => join.addressData.state,
  async () => {
    if (await debounce("join-check")) {
      const request = await api({
        url: "profile/flag/",
        method: "GET",
        params: { name: "dawaflag" },
      });
      if (request.ok && request.body.value) {
        console.log("state", join.addressData.state);
        if (join.addressData.state === "WA") {
          alert("We're not accepting new consultants from WA");
          join.addressData.state = "";
          join.addressData.zip_code = "";
        }
      }
    }
  },
  { deep: true },
);

onMounted(() => {
  addresses.reset();
  addresses.allowPOBoxes = true;

  if (join.addressData?.zip_code) {
    addresses.fetchCountyInfo(join.addressData?.zip_code, {
      existing: {
        city: join.addressData.city,
        state: join.addressData.state,
        county: join.addressData.county,
      },
    });
  }
});

async function finishForms() {
  step.value += 1;
}

function verifyPasswordForm() {
  passwordErrors.value = {};
  const error = validatePassword(join.passwordData.back_office_password);
  if (error) {
    passwordErrors.value = { back_office_password: [error] };
  }
  if (
    join.passwordData.back_office_password !==
    join.passwordData.confirm_password
  ) {
    passwordErrors.value = {
      ...passwordErrors.value,
      confirm_password: ["Passwords must match"],
    };
  }

  if (Object.values(passwordErrors.value).length === 0) {
    join.info.password = true;
    step.value += 1;
  }
}

function handleTermsScroll(event: Event) {
  const target = event.target as HTMLElement;
  const buffer = 5;
  if (target.scrollTop + target.clientHeight + buffer >= target.scrollHeight) {
    hasScrolledToBottomOfTerms.value = true;
  }
}
</script>

<style scoped>
.info-section {
  display: flex;
  margin: 32px 0;
  font-size: 14px;
  .info-column {
    margin-right: var(--3XL, 80px);
  }
  .name {
    display: block;
  }
}

.terms {
  height: 250px;
  width: 100%;
  border: 1px solid #c4c4c4;
  padding: 16px;
  overflow: scroll;
  color: var(--Black, #000);
  /* Paragraph 1 */
  font-family: Indivisible;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  :deep(p) {
    margin: 0;
  }
  :deep(p strong) {
    font-weight: 500;
  }
}

#app .auto-form :deep(.hrow .inside) {
  grid-template-columns: 1fr 1fr;
}

.my-account-info :deep(.auto-form .inside) {
  max-width: 740px !important;
}
.my-account-info {
  border-bottom: 1px solid var(--30, #c4c4c4);
  .name {
    color: var(--Black, #000);
    /* Paragraph 1 */
    font-family: Indivisible;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
  }
  h3 {
    color: var(--Black, #000);
    /* Heading 3 */
    font-family: Indivisible;
    font-size: 18px;
    font-style: normal;
    font-weight: 300;
    line-height: normal;
    text-transform: uppercase;
    margin: 0;
  }
}
.my-account-info:deep(p) {
  font-size: 14px;
  margin: 8px 0 24px;
}
.my-account-info h2 {
  text-align: left;
  align-items: center;
}
.my-account-info:deep(#pv_id_44.p-dropdown),
.my-account-info:deep(#pv_id_46.p-dropdown) {
  width: calc(50% - 11px);
}

.my-account-info:deep(.help) {
  font-size: 14px;
}
.enrollment-terms :deep(label) {
  text-transform: math-auto !important;
}
#app .auto-form .enrollment-terms :deep(label.flex-row.gap-sm.checkbox) {
  white-space: revert-layer !important;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

h3.enrollment-header {
  margin-bottom: var(--Sm, 24px) !important;
}

.p-button.grey.prev-grey {
  margin-right: var(--Sm, 24px);
}

#app input {
  border: 1px dashed green;
}

@media (min-width: 800px) {
  :deep(label[for="payout_method"]),
  :deep(label[for="payout_method"] + *) {
    width: calc(50% - 12px);
  }
  :deep(label[for="account_type"]),
  :deep(label[for="account_type"] + *) {
    width: calc(50% - 12px);
  }
}

@media (max-width: 800px) {
  .account-form :deep(.inside) {
    grid-template-columns: 1fr !important;
    grid-auto-flow: row !important;
  }
  .info-section {
    .info-column {
      margin-right: var(--Sm, 24px);
    }
  }
}
</style>
